import axiosInstance from '../axiosConfig';

export interface Donor {
    id: number;
    image: string;
    image_url: string;
    order: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface DonorsResponse {
    success: boolean;
    data: Donor[];
    message: string;
    count: number;
    meta?: {
        total: number;
        per_page: number;
        current_page: number;
        last_page: number;
    };
}

export interface DonorResponse {
    success: boolean;
    data: Donor;
    message: string;
}

export const donorService = {
    // Initialize CSRF token
    async initializeCsrf() {
        try {
            await axiosInstance.get('/sanctum/csrf-cookie');
            console.log('CSRF token initialized');
        } catch (error) {
            console.error('Failed to initialize CSRF token:', error);
        }
    },

    // Get all donors with pagination
    async getAll(params?: { page?: number; per_page?: number }): Promise<DonorsResponse> {
        try {
            const response = await axiosInstance.get('/api/admin/donors', { params });
            return response.data;
        } catch (error) {
            console.error('Error in getAll:', error);
            throw error;
        }
    },

    // Get single donor by ID
    async getById(id: number): Promise<DonorResponse> {
        try {
            const response = await axiosInstance.get(`/api/admin/donors/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error in getById:', error);
            throw error;
        }
    },

    // Create new donor
    async create(formData: FormData): Promise<DonorResponse> {
        try {
            const response = await axiosInstance.post('/api/admin/donors', formData);
            return response.data;
        } catch (error) {
            console.error('Error in create:', error);
            throw error;
        }
    },

    // Update donor
    async update(id: number, formData: FormData): Promise<DonorResponse> {
        try {
            formData.append('_method', 'PUT');
            const response = await axiosInstance.post(`/api/admin/donors/${id}`, formData);
            return response.data;
        } catch (error) {
            console.error('Error in update:', error);
            throw error;
        }
    },

    // Delete donor
    async delete(id: number): Promise<{ success: boolean; message: string }> {
        try {
            const response = await axiosInstance.delete(`/api/admin/donors/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error in delete:', error);
            throw error;
        }
    },

    // Bulk delete donors
    async bulkDelete(ids: number[]): Promise<{ success: boolean; message: string; deleted_count?: number }> {
        try {
            const response = await axiosInstance.post('/api/admin/donors/bulk-delete', { ids });
            return response.data;
        } catch (error) {
            console.error('Error in bulkDelete:', error);
            throw error;
        }
    },

    // Update status
    async updateStatus(id: number, isActive: boolean): Promise<{ success: boolean; message: string; data: any }> {
        try {
            const response = await axiosInstance.patch(`/api/admin/donors/${id}/status`, {
                is_active: isActive
            });
            return response.data;
        } catch (error) {
            console.error('Error in updateStatus:', error);
            throw error;
        }
    },

    // Reorder donors
    async reorder(orders: { id: number; order: number }[]): Promise<{ success: boolean; message: string }> {
        try {
            const response = await axiosInstance.post('/api/admin/donors/reorder', { orders });
            return response.data;
        } catch (error) {
            console.error('Error in reorder:', error);
            throw error;
        }
    }
};